// src/pages/PortfolioWaterRisk.tsx
import siteMap from '../../../siteMap'
import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../../../store'
import { useHistory } from 'react-router-dom'
import { Card, CardContent, Chip, Grid, Stack, Typography, useTheme, Button } from '@mui/material'
import { DataGrid, GridColDef, GridRowId, GridToolbarContainer, useGridApiContext } from '@mui/x-data-grid'
import { useLocations } from '../../../hooks/locations'
import { usePortfolioClimateData } from '../../../hooks/climateData'
import {
  EmissionTier,
  MetricState,
  stateColorMapping,
  canonicalPeriodLabels,
  metricStateLabels,
  PERIODS,
  CanonicalPeriod,
} from '../common'
import { WaterRiskScoreBadge } from '../common/waterRisk/utils'
import FilterAltIcon from '@mui/icons-material/FilterAlt'

function StatusChip({ value }: { value?: MetricState | null }) {
  if (!value) return <span>—</span>
  const label = metricStateLabels[value] || value
  const color = stateColorMapping[value]
  return (
    <span style={{ background: color, color: 'white', fontSize: 11, padding: '2px 6px', borderRadius: 4 }}>
      {label}
    </span>
  )
}

function WaterRiskCategoryCell({ category, description }: { category?: number | null; description?: string | null }) {
  const score = category ?? null
  if (score == null && !description) return <span>—</span>

  return (
    <Stack>
      <WaterRiskScoreBadge score={score} size="dense" />
    </Stack>
  )
}

function PropertyFilterToolbar() {
  const apiRef = useGridApiContext()
  return (
    <GridToolbarContainer sx={{ justifyContent: 'space-between', px: 0, py: 1 }}>
      <Typography variant="subtitle1">Property Breakdown</Typography>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
        <Button
          variant="outlined"
          size="small"
          startIcon={<FilterAltIcon />}
          onClick={() => apiRef.current?.showFilterPanel?.()}>
          Filter
        </Button>
      </Stack>
    </GridToolbarContainer>
  )
}

type Row = {
  id: number
  name: string
  type: string
  ws: {
    category_short: number | null
    category_mid: number | null
    category_long: number | null
    desc_short: string | null
    desc_mid: string | null
    desc_long: string | null
  } | null
  wsState?: MetricState
}

function PortfolioWaterRisk({ emissionTier }: { emissionTier: EmissionTier }) {
  const theme = useTheme()
  const history = useHistory()
  const { groupId } = useSelector((state: RootState) => state.system)
  const goToProperty = useCallback(
    (propertyId: GridRowId) => history.push(siteMap.PropertyClimateAnalytics.getPath(propertyId, 'wr', emissionTier)),
    [emissionTier, history],
  )

  // Data
  const { data: locations } = useLocations({ group_id: groupId })
  const { data: wsInfoByProperty } = usePortfolioClimateData('wr:ws_info', { groupId: groupId ?? undefined })

  const rows: Row[] = useMemo(() => {
    const base: Record<number, Row> = {}
    ;(locations || []).forEach((p: any) => {
      base[p.id] = {
        id: p.id,
        name: p.name || `Property ${p.id}`,
        type: p.type.label,
        ws: null,
      }
    })

    const tierKey = emissionTier as unknown as string // 'low' | 'moderate' | 'high'

    function pick(entry: any, variable: 'category' | 'description', period: CanonicalPeriod) {
      if (!entry || entry.state?.value !== MetricState.SUCCEEDED || !entry.data) return null
      const node = entry.data?.data?.[tierKey]?.[variable]?.[period]
      if (!node) return null
      const y = node.y
      if (variable === 'category') {
        const n = typeof y === 'string' ? parseFloat(y) : typeof y === 'number' ? y : NaN
        return Number.isFinite(n) ? n : null
      } else {
        return typeof y === 'string' ? y : y != null ? String(y) : null
      }
    }

    if (wsInfoByProperty && typeof wsInfoByProperty === 'object') {
      Object.values(wsInfoByProperty as Record<string, any>).forEach(entry => {
        const r = base[entry.property_id] || { id: entry.property_id, name: `Property ${entry.property_id}`, ws: null }
        r.wsState = entry.state?.value
        if (entry?.state?.value === MetricState.SUCCEEDED && entry?.data) {
          r.ws = {
            category_short: pick(entry, 'category', CanonicalPeriod.SHORT_TERM) as number | null,
            desc_short: pick(entry, 'description', CanonicalPeriod.SHORT_TERM) as string | null,
            category_mid: pick(entry, 'category', CanonicalPeriod.MID_TERM) as number | null,
            desc_mid: pick(entry, 'description', CanonicalPeriod.MID_TERM) as string | null,
            category_long: pick(entry, 'category', CanonicalPeriod.LONG_TERM) as number | null,
            desc_long: pick(entry, 'description', CanonicalPeriod.LONG_TERM) as string | null,
          }
        } else {
          r.ws = null
        }
        base[entry.property_id] = r
      })
    }

    return Object.values(base)
  }, [locations, emissionTier, wsInfoByProperty])

  // KPIs:
  // - Majority Category per period
  // - High / Very High count per period (category >= 3)
  // - Average Score per period (score = category + 1)
  const kpis = useMemo(() => {
    type Agg = {
      majorityCategory: number | null
      majorityLabel: string | null
      highCount: number
      avgScore: number | null
      total: number
    }
    const out: Record<CanonicalPeriod, Agg> = {
      [CanonicalPeriod.SHORT_TERM]: {
        majorityCategory: null,
        majorityLabel: null,
        highCount: 0,
        avgScore: null,
        total: 0,
      },
      [CanonicalPeriod.MID_TERM]: {
        majorityCategory: null,
        majorityLabel: null,
        highCount: 0,
        avgScore: null,
        total: 0,
      },
      [CanonicalPeriod.LONG_TERM]: {
        majorityCategory: null,
        majorityLabel: null,
        highCount: 0,
        avgScore: null,
        total: 0,
      },
    }

    const catsFor = (p: CanonicalPeriod) =>
      rows
        .map(r => {
          const ws = r.ws
          if (!ws) return null
          return p === CanonicalPeriod.SHORT_TERM
            ? ws.category_short
            : p === CanonicalPeriod.MID_TERM
            ? ws.category_mid
            : ws.category_long
        })
        .filter((v): v is number => typeof v === 'number')

    const labelFor = (cat: number, p: CanonicalPeriod): string | null => {
      const labels =
        rows
          .map(r => {
            const ws = r.ws
            if (!ws) return null
            const l =
              p === CanonicalPeriod.SHORT_TERM
                ? ws.desc_short
                : p === CanonicalPeriod.MID_TERM
                ? ws.desc_mid
                : ws.desc_long
            const c =
              p === CanonicalPeriod.SHORT_TERM
                ? ws.category_short
                : p === CanonicalPeriod.MID_TERM
                ? ws.category_mid
                : ws.category_long
            return c === cat ? l : null
          })
          .filter((s): s is string => typeof s === 'string') || []
      const map = new Map<string, number>()
      labels.forEach(s => map.set(s, (map.get(s) || 0) + 1))
      let best: string | null = null
      let bestN = -1
      map.forEach((n, s) => {
        if (n > bestN) {
          best = s
          bestN = n
        }
      })
      return best
    }

    PERIODS.forEach(p => {
      const cats = catsFor(p)
      // Majority
      const counts = new Map<number, number>()
      cats.forEach(c => counts.set(c, (counts.get(c) || 0) + 1))
      let modeCat: number | null = null
      let modeN = -1
      counts.forEach((n, c) => {
        if (n > modeN) {
          modeCat = c
          modeN = n
        }
      })
      // High / Very High (category >= 3)
      const highCount = cats.filter(c => c >= 3).length
      // Average score (category+1)
      const scores = cats.map(c => c + 1)
      const avgScore = scores.length ? scores.reduce((a, b) => a + b, 0) / scores.length : null

      out[p] = {
        majorityCategory: modeCat,
        majorityLabel: modeCat != null ? labelFor(modeCat, p) : null,
        highCount,
        avgScore,
        total: cats.length,
      }
    })
    return out
  }, [rows])

  // Columns
  const columns: GridColDef<Row>[] = useMemo(
    () => [
      { field: 'name', headerName: 'Property', width: 240 },
      { field: 'type', headerName: 'Property Type', width: 130 },
      // 2030
      {
        field: 'desc_short',
        headerName: canonicalPeriodLabels[CanonicalPeriod.SHORT_TERM],
        width: 220,
        valueGetter: p => {
          const value = p.row.ws?.category_short
          return typeof value === 'number' ? value : null
        },
        renderCell: p => (
          <WaterRiskCategoryCell category={p.value as number | null} description={p.row.ws?.desc_short ?? null} />
        ),
      },
      // 2050
      {
        field: 'desc_mid',
        headerName: canonicalPeriodLabels[CanonicalPeriod.MID_TERM],
        width: 220,
        valueGetter: p => {
          const value = p.row.ws?.category_mid
          return typeof value === 'number' ? value : null
        },
        renderCell: p => (
          <WaterRiskCategoryCell category={p.value as number | null} description={p.row.ws?.desc_mid ?? null} />
        ),
      },
      // 2080/2100 (canonical 'long')
      {
        field: 'desc_long',
        headerName: canonicalPeriodLabels[CanonicalPeriod.LONG_TERM],
        width: 220,
        valueGetter: p => {
          const value = p.row.ws?.category_long
          return typeof value === 'number' ? value : null
        },
        renderCell: p => (
          <WaterRiskCategoryCell category={p.value as number | null} description={p.row.ws?.desc_long ?? null} />
        ),
      },
      {
        field: 'statusWS',
        headerName: 'Status',
        width: 140,
        sortable: false,
        valueGetter: p => p.row.wsState ?? null,
        renderCell: p => <StatusChip value={p.value as MetricState | null} />,
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 140,
        sortable: false,
        filterable: false,
        renderCell: params => (
          <Button size="small" onClick={() => goToProperty(params.row.id)}>
            View property
          </Button>
        ),
      },
    ],
    [goToProperty],
  )

  return (
    <Stack alignItems={'center'} pb={6}>
      <Stack sx={{ maxWidth: theme.breakpoints.values?.['lg'], width: '100%' }} spacing={2}>
        {/* KPI strip */}
        <Grid container spacing={2}>
          {/* Average Score per period */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="overline">Average Risk Rating</Typography>
                <Stack direction="column" spacing={1} mt={1}>
                  {PERIODS.map(period => (
                    <Stack key={period} direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                      <Typography variant="body2">{canonicalPeriodLabels[period]}</Typography>
                      <WaterRiskScoreBadge score={kpis[period]?.avgScore ?? null} size="dense" />
                    </Stack>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* High / Very High counts per period */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="overline"># of High / Very High Risk Ratings</Typography>
                <Stack direction="column" spacing={1} mt={1}>
                  {PERIODS.map(period => {
                    const { highCount, total } = kpis[period]
                    const pct = total ? Math.round((highCount / total) * 100) : null
                    return (
                      <Stack
                        key={period}
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="space-between">
                        <Typography variant="body2">{canonicalPeriodLabels[period]}</Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="caption" color="text.secondary">
                            {pct != null ? `${pct}% of properties` : '—'}
                          </Typography>
                          <Chip label={highCount} />
                        </Stack>
                      </Stack>
                    )
                  })}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Majority Category per period */}
          {/* <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="overline">Most Common Category</Typography>
                <Stack direction="column" spacing={1} mt={1}>
                  {PERIODS.map(period => {
                    const cat = kpis[period].majorityCategory
                    const label = kpis[period].majorityLabel
                    const labelsOverride = cat && label ? { [cat]: label } : undefined
                    return (
                      <Stack
                        key={period}
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="space-between">
                        <Typography variant="body2">{canonicalPeriodLabels[period]}</Typography>
                        <WaterRiskScoreBadge score={cat} size="dense" labelsOverride={labelsOverride} />
                      </Stack>
                    )
                  })}
                </Stack>
              </CardContent>
            </Card>
          </Grid> */}
        </Grid>

        {/* Table */}
        <Card>
          <CardContent>
            <div style={{ height: 560, width: '100%' }}>
              <DataGrid
                rows={rows}
                columns={columns}
                getRowId={r => r.id}
                initialState={{
                  sorting: { sortModel: [{ field: 'desc_long', sort: 'desc' }] },
                  columns: { columnVisibilityModel: { statusWS: false } },
                }}
                density="compact"
                onRowDoubleClick={params => goToProperty(params.id)}
                disableSelectionOnClick
                components={{ Toolbar: PropertyFilterToolbar }}
                sx={{
                  '& .MuiDataGrid-row': { cursor: 'pointer' },
                  '& .MuiDataGrid-row:hover': { backgroundColor: theme.palette.action.hover },
                }}
              />
            </div>
          </CardContent>
        </Card>
      </Stack>
    </Stack>
  )
}

export default PortfolioWaterRisk
